import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdvanceDeduction, ExternalAdvance, ServiceItem } from '../../types';
import {
    createExternalAdvance,
    deleteExternalAdvance,
    fetchExternalAdvances,
    fetchProjectInvoices,
    updateExternalAdvance,
} from '../../services/invoiceService';
import { deductionsFromPriorInvoices } from '../../services/advanceInvoiceLogic';
import {
    aggregateExistingAdvances,
    BACKFILL_TARGET_NEW,
    BACKFILL_TARGET_UNSET,
    backfillReferenceFor,
    BackfillSelection,
    externalAdvanceNumberConflict,
    extractedAdvancesFromAi,
    extractedInvoiceMetaFromAi,
    extractPositionRows,
    hasUnassignedBackfillRows,
    initialBackfillTarget,
    isBackfillPreselected,
    positionsSumDeviation,
    discountFromDeviation,
    SumDeviation,
    ReconciledAdvance,
    ReconciledPosition,
    reconcileAdvances,
    reconcilePositions,
    SkippedPositionRow,
    unitMismatch,
} from '../../services/externalAdvances';
import {
    centsFromEuro,
    centsFromEuroInput,
    formatCents,
    formatCentsPlain,
    formatTaxRateBp,
    percentFromTaxRateBp,
    roundCents,
    sumCents,
    taxRateBpFromPercent,
} from '../../services/money';
import { extractLastInvoice } from '../../services/geminiService';
import { describeError } from '../../services/errorMessage';
import { Button } from '../Button';
import { BUCHHALTUNG_NUR_LESEN_HINWEIS } from './rechnungRechte';
import {
    AlertTriangle, Check, CheckCircle2, FileText, Loader2, Pencil, Plus,
    Receipt, Trash2, Upload, X,
} from 'lucide-react';

/**
 * Vorrechnungen eines Projekts: App-Belege (schreibgeschuetzt) und extern
 * geschriebene Alt-Abschlaege (nachtragbar). Die Summenzeile ist exakt der
 * Betrag, den die naechste kumulative Rechnung abziehen wird —
 * createFinalInvoice speist sich aus denselben Quellen.
 */

interface Props {
    projectId: string;
    /** Vorbelegung neuer Eintraege, aus der Rechnungskonfiguration. */
    defaultTaxRateBp?: number;
    /**
     * Ist-Stand des Projekt-LV. Abgleichsgrundlage fuer die Alt-Positionen aus
     * "Letzte Rechnung einlesen" — und damit die Idempotenz-Schranke: was schon
     * im Projekt steht, wird nicht noch einmal angelegt.
     */
    projectServices?: ServiceItem[];
    /**
     * Belegnummern, deren Alt-Positionen bereits per Backfill uebernommen
     * wurden (bookingReference 'ALT-<Nummer>' im serviceLog des Projekts).
     *
     * Idempotenz-Riegel UEBER die Belegnummer, nicht nur ueber das
     * OCR-Titel-Matching in reconcilePositions: eine erneut eingelesene
     * Alt-Rechnung soll gewarnt und mit abgewaehlten Zeilen starten, auch
     * wenn eine Position aus irgendeinem Grund nicht als 'matched' erkannt
     * wird. Optional — ohne die Prop bleibt das Panel wie zuvor.
     */
    usedBackfillReferences?: Set<string>;
    /**
     * Uebernahme nachgetragener Alt-Positionen ins Projekt.
     *
     * Bewusst ein Callback des Hosts statt eines eigenen Direkt-Writes: das
     * Projekt wird ueber loadProjectById → saveProjectToCloud geschrieben, den
     * einzigen Weg, der auch die relationalen Kind-Tabellen mitpflegt.
     * Ohne den Callback bleibt die Positions-Sektion lesend.
     */
    onBackfillPositions?: (payload: {
        selections: BackfillSelection[];
        serviceDate: string;
        invoiceNumber: string;
        /** Auftraggeber des Altbelegs; leer bei ungeteilten Projekten. */
        customerId?: string;
        /**
         * Teilnehmer der Aufteilung, dem der Altbeleg gehoert. Feiner als
         * `customerId` und bei mehreren Haeusern desselben Auftraggebers die
         * einzige eindeutige Angabe. Wird hier seit jeher mitgeschickt und vom
         * Empfaenger ausgewertet — nur die Deklaration hatte sie nicht gefuehrt.
         */
        partyId?: string;
    }) => Promise<void>;
    /**
     * Ruecknahme des Leistungsnachtrags beim Loeschen eines Abschlags.
     *
     * Derselbe Import schreibt zwei Dinge: den BETRAG nach external_advances
     * und die LEISTUNG ins Projekt (Menge je Position plus ALT-Protokollzeile).
     * Ohne diesen Callback loescht das Panel nur den Betrag, und die Leistung
     * bleibt doppelt im Leistungsstand stehen. Wie beim Nachtrag liegt der
     * Schreibzugriff beim Host — nur dessen Speicherpfad pflegt die
     * relationalen Kind-Tabellen mit. Ohne die Prop warnt das Loeschen stattdessen.
     */
    onRevertBackfill?: (invoiceNumber: string, customerId?: string) => Promise<void>;
    /**
     * Auftraggeber des Aufteilungsschluessels — leer bei ungeteilten Projekten.
     *
     * Ab ZWEI Beteiligten deckt eine Alt-Rechnung nur den Anteil EINES
     * Auftraggebers ab. Ohne seine Angabe liesse sich der Abrechnungsstand je
     * Auftraggeber nicht bilden: die Menge zaehlte als Altbestand ohne
     * Zuordnung und sperrte jede weitere Teilrechnung. Deshalb ist die
     * Auswahl dann Pflicht und nicht nur eine Angabe.
     */
    splitParticipants?: { partyId: string; customerId: string; name: string }[] | null;
    /**
     * Lesemodus der Buchhaltung (ohne Recht „Rechnungen bearbeiten“): die
     * Liste bleibt, Nachtragen, Einlesen, Ändern und Löschen sind gesperrt.
     */
    nurLesen?: boolean;
}

const de =(iso?: string): string => (iso ? iso.split('-').reverse().join('.') : '—');

interface FormState {
    invoiceNumber: string;
    invoiceDate: string;
    grossInput: string;
    taxPercent: string;
    note: string;
    /**
     * Auftraggeber des Altbelegs. Leer heisst "nicht zugeordnet" — bei einem
     * geteilten Projekt sperrt das die Teilrechnung, denn der Abzug liesse sich
     * dann weder einem zuschlagen noch anteilig verteilen, ohne zu raten.
     */
    customerId: string;
    /** Der Teilnehmer der Aufteilung (project_splits.id). */
    partyId: string;
}

const EMPTY_FORM: FormState = {
    invoiceNumber: '',
    invoiceDate: '',
    grossInput: '',
    taxPercent: '19',
    note: '',
    customerId: '',
    partyId: '',
};

export const ExternalAdvancesPanel: React.FC<Props> = ({
    projectId,
    defaultTaxRateBp = 1900,
    projectServices,
    usedBackfillReferences,
    onBackfillPositions,
    onRevertBackfill,
    splitParticipants,
    nurLesen = false,
}) => {
    /** Titel der gesperrten Knöpfe im Lesemodus; fehlt sonst. */
    const gesperrt = nurLesen ? BUCHHALTUNG_NUR_LESEN_HINWEIS : undefined;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [appDeductions, setAppDeductions] = useState<AdvanceDeduction[]>([]);
    const [externals, setExternals] = useState<ExternalAdvance[]>([]);
    // ALLE Belegnummern des Projekts — auch stornierte/nicht abzugsfaehige:
    // eine solche Nummer als "extern" nachzutragen zoege einen Beleg ab, den
    // die App bewusst nicht (mehr) abzieht.
    const [appNumbers, setAppNumbers] = useState<string[]>([]);

    const [form, setForm] = useState<FormState>({
        ...EMPTY_FORM,
        taxPercent: String(percentFromTaxRateBp(defaultTaxRateBp)),
    });
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);

    const [showImport, setShowImport] = useState(false);

    const reload = async () => {
        setLoading(true);
        setError(null);
        try {
            const [invoices, ext] = await Promise.all([
                fetchProjectInvoices(projectId),
                fetchExternalAdvances(projectId),
            ]);
            setAppDeductions(deductionsFromPriorInvoices(invoices));
            setAppNumbers(invoices.map((i) => i.invoiceNumber).filter((n): n is string => Boolean(n)));
            setExternals(ext);
        } catch (e) {
            setError(describeError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // App-Belege je Nummer (ein Beleg ueber zwei Steuersaetze = eine Zeile);
    // das Datum haengt am Beleg, nicht an der Steuerzeile.
    const appRows = useMemo(() => {
        const dateByNumber = new Map<string, string>();
        for (const d of appDeductions) {
            if (!dateByNumber.has(d.invoiceNumber)) dateByNumber.set(d.invoiceNumber, d.invoiceDate);
        }
        return aggregateExistingAdvances(appDeductions, []).map((r) => ({
            ...r,
            invoiceDate: dateByNumber.get(r.invoiceNumber),
        }));
    }, [appDeductions]);

    const totalGrossCents = useMemo(
        () =>
            sumCents(appDeductions.map((d) => d.grossCents)) +
            sumCents(externals.map((e) => e.grossCents)),
        [appDeductions, externals],
    );

    const startCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM, taxPercent: String(percentFromTaxRateBp(defaultTaxRateBp)) });
        setShowForm(true);
    };

    const startEdit = (e: ExternalAdvance) => {
        setEditingId(e.id);
        setForm({
            invoiceNumber: e.invoiceNumber,
            invoiceDate: e.invoiceDate ?? '',
            grossInput: formatCentsPlain(e.grossCents),
            taxPercent: String(percentFromTaxRateBp(e.taxRateBp)),
            note: e.note ?? '',
            customerId: e.customerId ?? '',
            partyId: e.splitPartyId ?? '',
        });
        setShowForm(true);
    };

    const submitForm = async () => {
        if (nurLesen) return;
        const grossCents = centsFromEuroInput(form.grossInput);
        const taxPercent = Number(form.taxPercent.replace(',', '.'));
        if (!form.invoiceNumber.trim()) return setError('Bitte die Rechnungsnummer des Altbelegs angeben.');
        if (grossCents == null || grossCents <= 0) return setError('Bitte einen Bruttobetrag größer 0 angeben.');
        if (!Number.isFinite(taxPercent) || taxPercent < 0) return setError('Bitte einen gültigen Steuersatz angeben.');
        // Kollision ueber BEIDE Quellen — der DB-Unique-Index kennt nur die
        // externen Eintraege; eine App-Belegnummer wuerde doppelt abgezogen.
        const conflict = externalAdvanceNumberConflict(
            form.invoiceNumber,
            appNumbers,
            externals,
            editingId ?? undefined,
        );
        if (conflict === 'app')
            return setError(
                `Die Nummer ${form.invoiceNumber.trim()} gehört zu einer Rechnung aus der App — sie wird bereits automatisch berücksichtigt und darf nicht zusätzlich nachgetragen werden.`,
            );
        if (conflict === 'external')
            return setError(
                `Die Nummer ${form.invoiceNumber.trim()} ist bereits als externer Abschlag hinterlegt — bitte den bestehenden Eintrag bearbeiten.`,
            );
        setBusy(true);
        setError(null);
        try {
            const payload = {
                projectId,
                invoiceNumber: form.invoiceNumber.trim(),
                invoiceDate: form.invoiceDate || undefined,
                grossCents,
                taxRateBp: taxRateBpFromPercent(taxPercent),
                note: form.note.trim() || undefined,
                // Leer bleibt leer: ein Altbeleg ohne Aufteilung braucht keinen
                // Kunden, und einen zu erfinden waere schlimmer als keiner.
                customerId: form.customerId || undefined,
                splitPartyId: form.partyId || undefined,
            };
            if (editingId) await updateExternalAdvance(editingId, payload);
            else await createExternalAdvance(payload);
            setShowForm(false);
            setEditingId(null);
            await reload();
        } catch (e) {
            setError(describeError(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (e: ExternalAdvance) => {
        if (nurLesen) return;
        // Hat derselbe Beleg Leistung ins Projekt nachgetragen? Dann haengt am
        // Loeschen mehr als der Betrag.
        // Die Referenz traegt bei geteilten Projekten die Kundenkennung —
        // sonst traefe die Pruefung den Nachtrag eines anderen Beteiligten.
        const mitNachtrag = Boolean(
            usedBackfillReferences?.has(backfillReferenceFor(e.invoiceNumber, e.customerId)),
        );
        const nachtragZuruecknehmbar = mitNachtrag && Boolean(onRevertBackfill);
        const frage = !mitNachtrag
            ? `Nachgetragenen Abschlag ${e.invoiceNumber} löschen? Künftige Rechnungen ziehen ihn dann nicht mehr ab; bereits ausgestellte Belege bleiben unverändert.`
            : nachtragZuruecknehmbar
                ? `Nachgetragenen Abschlag ${e.invoiceNumber} löschen? Die aus diesem Beleg übernommene Leistung wird dabei ebenfalls zurückgenommen — erledigte und abgerechnete Menge sinken wieder um die nachgetragene Menge. Künftige Rechnungen ziehen den Abschlag nicht mehr ab; bereits ausgestellte Belege bleiben unverändert.`
                : `Nachgetragenen Abschlag ${e.invoiceNumber} löschen? ACHTUNG: Die aus diesem Beleg übernommene Leistung bleibt im Projekt stehen und zählt dann doppelt — sie muss in der Buchhaltungsansicht von Hand korrigiert werden.`;
        if (!window.confirm(frage)) return;
        setBusy(true);
        setError(null);
        try {
            // Erst die Leistung, dann der Betrag. Scheitert das Zuruecknehmen,
            // steht der Abschlag noch da und die naechste kumulative Rechnung
            // meldet den Widerspruch. Andersherum bliebe die doppelte Menge
            // still stehen — der Weg, auf dem in Projekt 26051007 aus 11
            // abgerechneten Begehungen 22 wurden.
            if (nachtragZuruecknehmbar) await onRevertBackfill!(e.invoiceNumber, e.customerId);
            await deleteExternalAdvance(e.id);
            await reload();
        } catch (err) {
            setError(describeError(err));
        } finally {
            setBusy(false);
        }
    };

    const inputClass =
        'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500';

    return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
            <div className="bg-slate-50 p-4 border-b border-slate-200 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Receipt className="w-4 h-4" /> Vorrechnungen (Abzug der nächsten Rechnung)
                </span>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setShowImport(true)} disabled={nurLesen} title={gesperrt} icon={<Upload className="w-4 h-4" />}>
                        Letzte Rechnung einlesen
                    </Button>
                    <Button variant="secondary" onClick={startCreate} disabled={nurLesen} title={gesperrt} icon={<Plus className="w-4 h-4" />}>
                        Abschlag nachtragen
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : (
                <>
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs">
                            <tr>
                                <th className="p-3">Nummer</th>
                                <th className="p-3">Datum</th>
                                <th className="p-3">Quelle</th>
                                <th className="p-3 text-right">Brutto</th>
                                <th className="p-3 w-20"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {appRows.map((r) => (
                                <tr key={`app-${r.invoiceNumber}`}>
                                    <td className="p-3 font-mono">{r.invoiceNumber}</td>
                                    <td className="p-3 text-slate-500">{de(r.invoiceDate)}</td>
                                    <td className="p-3"><span className="text-[11px] font-bold uppercase bg-indigo-50 text-indigo-600 rounded px-2 py-0.5">App</span></td>
                                    <td className="p-3 text-right font-mono">{formatCents(r.grossCents)}</td>
                                    <td className="p-3"></td>
                                </tr>
                            ))}
                            {externals.map((e) => (
                                <tr key={e.id}>
                                    <td className="p-3 font-mono">
                                        {e.invoiceNumber}
                                        {e.note && <div className="text-[11px] text-slate-400 font-sans">{e.note}</div>}
                                        {(splitParticipants?.length ?? 0) > 1 && (
                                            <div className="text-[11px] font-sans">
                                                {/* Erst ueber den Teilnehmer, dann ueber
                                                    den Auftraggeber: Altbelege von vor den
                                                    Abschnitten tragen nur diesen, und solange
                                                    er eindeutig ist, ist das eine Auskunft. */}
                                                {e.splitPartyId || e.customerId ? (
                                                    <span className="text-slate-400">
                                                        {splitParticipants?.find((t) =>
                                                            e.splitPartyId
                                                                ? t.partyId === e.splitPartyId
                                                                : t.customerId === e.customerId,
                                                        )?.name ?? 'anderer Teilnehmer'}
                                                    </span>
                                                ) : (
                                                    <span className="font-semibold text-amber-700">nicht zugeordnet — sperrt die Teilrechnung</span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-3 text-slate-500">{de(e.invoiceDate)}</td>
                                    <td className="p-3"><span className="text-[11px] font-bold uppercase bg-amber-50 text-amber-600 rounded px-2 py-0.5">Nachgetragen</span></td>
                                    <td className="p-3 text-right font-mono">{formatCents(e.grossCents)}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <button onClick={() => startEdit(e)} disabled={busy || nurLesen} className="text-slate-400 hover:text-indigo-600 p-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-slate-400" title={gesperrt ?? 'Bearbeiten'}><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => void remove(e)} disabled={busy || nurLesen} className="text-slate-400 hover:text-red-500 p-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-slate-400" title={gesperrt ?? 'Löschen'}><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                            {appRows.length === 0 && externals.length === 0 && (
                                <tr><td colSpan={5} className="p-6 text-center text-slate-400 italic">Keine Vorrechnungen — die nächste kumulative Rechnung zieht nichts ab.</td></tr>
                            )}
                        </tbody>
                        {(appRows.length > 0 || externals.length > 0) && (
                            <tfoot>
                                <tr className="bg-slate-50 font-semibold text-slate-700">
                                    <td className="p-3" colSpan={3}>Summe Vorrechnungen</td>
                                    <td className="p-3 text-right font-mono">{formatCents(totalGrossCents)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                    <p className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                        Änderungen wirken nur auf künftige Belege — bereits ausgestellte Rechnungen behalten den Abzug, mit dem sie ausgestellt wurden.
                    </p>
                </>
            )}

            {error && (
                <p className="px-4 pb-3 text-xs text-red-600 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{error}</p>
            )}

            {showForm && !nurLesen && (
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Rechnungsnummer *</label>
                            <input type="text" className={inputClass} value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Datum</label>
                            <input type="date" className={inputClass} value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Brutto (€) *</label>
                            <input type="text" inputMode="decimal" placeholder="z.B. 1.190,00" className={inputClass} value={form.grossInput} onChange={(e) => setForm({ ...form, grossInput: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">USt. (%)</label>
                            <input type="number" min={0} step={0.1} className={inputClass} value={form.taxPercent} onChange={(e) => setForm({ ...form, taxPercent: e.target.value })} />
                        </div>
                    </div>
                    {(splitParticipants?.length ?? 0) > 1 && (
                        <div className="mt-3">
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Gehört zu *</label>
                            {/* Gewaehlt wird der TEILNEHMER und nicht der
                                Auftraggeber: hat ein Auftraggeber mehrere
                                Abschnitte, waere der Abzug zwischen ihnen nicht
                                zu entscheiden — die Rechnung fuer Haus B zoege
                                den Abschlag von Haus A mit ab. */}
                            <select
                                className={inputClass}
                                value={form.partyId}
                                onChange={(e) => {
                                    const gewaehlt = (splitParticipants ?? []).find(
                                        (t) => t.partyId === e.target.value,
                                    );
                                    setForm({
                                        ...form,
                                        partyId: e.target.value,
                                        // Der Auftraggeber wandert mit: er bleibt
                                        // die Angabe, die Debitor und Auswertungen
                                        // ausserhalb der Aufteilung lesen.
                                        customerId: gewaehlt?.customerId ?? '',
                                    });
                                }}
                            >
                                <option value="">— nicht zugeordnet —</option>
                                {(splitParticipants ?? []).map((teilnehmer) => (
                                    <option key={teilnehmer.partyId} value={teilnehmer.partyId}>{teilnehmer.name}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-[11px] text-slate-400">
                                Das Projekt ist aufgeteilt. Ein Abzugsposten ohne Zuordnung sperrt
                                die Teilrechnung — sein Abzug ließe sich sonst nur raten, und
                                geraten wird hier an einem Geldbetrag.
                            </p>
                        </div>
                    )}
                    <div className="mt-3">
                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Notiz</label>
                        <input type="text" className={inputClass} placeholder="optional, nur intern" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => { setShowForm(false); setEditingId(null); }}>Abbrechen</Button>
                        <Button variant="primary" onClick={() => void submitForm()} disabled={busy} icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}>
                            {editingId ? 'Speichern' : 'Nachtragen'}
                        </Button>
                    </div>
                </div>
            )}

            {showImport && !nurLesen && (
                <LastInvoiceImportModal
                    appDeductions={appDeductions}
                    externals={externals}
                    defaultTaxRateBp={defaultTaxRateBp}
                    projectServices={projectServices ?? []}
                    usedBackfillReferences={usedBackfillReferences}
                    canBackfillPositions={Boolean(onBackfillPositions)}
                    splitParticipants={splitParticipants}
                    onClose={() => setShowImport(false)}
                    onApply={async ({ advances: rows, positions, serviceDate, invoiceNumber, discountCents, customerId, partyId }) => {
                        setBusy(true);
                        setError(null);
                        try {
                            // Reihenfolge: ERST die Positionen ins Projekt, DANN
                            // die Abschlaege. Bricht der zweite Schritt ab, steht
                            // Leistung ohne Abzug im Projekt — die naechste
                            // Rechnung faellt zu hoch aus und faellt auf.
                            // Umgekehrt stuende der Abzug ohne Bemessungs-
                            // grundlage da und blockierte jeden weiteren Beleg.
                            if (positions.length && onBackfillPositions) {
                                await onBackfillPositions({
                                    selections: positions,
                                    serviceDate,
                                    invoiceNumber,
                                    customerId,
                                    partyId,
                                });
                            }
                            // Der Abgleich im Modal kennt nur ABZUGSFAEHIGE
                            // App-Belege — eine stornierte App-Nummer kaeme als
                            // "neu" durch und wuerde faelschlich abgezogen.
                            const skipped: string[] = [];
                            for (const r of rows) {
                                if (externalAdvanceNumberConflict(r.invoiceNumber, appNumbers, externals)) {
                                    skipped.push(r.invoiceNumber);
                                    continue;
                                }
                                await createExternalAdvance({
                                    projectId,
                                    invoiceNumber: r.invoiceNumber,
                                    invoiceDate: r.invoiceDate,
                                    grossCents: r.grossCents,
                                    taxRateBp: r.taxRateBp,
                                    // Der Abzug gehoert dem Auftraggeber des
                                    // Belegs. Ohne ihn zoege ihn spaeter JEDE
                                    // Teilrechnung ab, auch die der uebrigen
                                    // Beteiligten.
                                    customerId,
                                    splitPartyId: partyId,
                                    // Der Nachlass gehoert in den Vermerk, nicht in den
                                    // Betrag: gefordert wurde, was in grossCents steht.
                                    note: discountCents > 0
                                        ? `Aus letzter Rechnung eingelesen · Nachlass ${formatCents(discountCents)} netto`
                                        : 'Aus letzter Rechnung eingelesen',
                                });
                            }
                            if (skipped.length) {
                                setError(
                                    `Nicht übernommen (Nummer existiert bereits als App-Beleg dieses Projekts): ${skipped.join(', ')}.`,
                                );
                            }
                            setShowImport(false);
                            await reload();
                        } catch (e) {
                            setError(describeError(e));
                            // WEITERGEBEN, nicht nur hier ablegen: diese
                            // Meldung steht HINTER dem noch offenen Dialog und
                            // ist damit unsichtbar. Der Dialog faengt den Wurf
                            // und zeigt den Grund in seiner Fusszeile — ohne
                            // ihn sah ein Fehlschlag der Uebernahme aus wie ein
                            // Klick, der nichts tut.
                            throw e;
                        } finally {
                            setBusy(false);
                        }
                    }}
                />
            )}
        </div>
    );
};

// ── Import-Modal: Letzte Rechnung einlesen ──────────────────────────────────

const MAX_BYTES = 18 * 1024 * 1024;

/**
 * Nimmt die Erkennung diese Datei ueberhaupt an?
 *
 * Manche Systeme liefern beim Ziehen einen leeren MIME-Typ; dann entscheidet
 * die Endung, statt die Datei blind durchzulassen.
 */
const isSupportedFile = (file: File): boolean => {
    const type = (file.type || '').toLowerCase();
    if (type) return type === 'application/pdf' || type.startsWith('image/');
    return /\.(pdf|png|jpe?g|webp|heic|heif|gif|bmp|tiff?)$/i.test(file.name || '');
};

const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
        reader.readAsDataURL(file);
    });

type ReviewRow = ReconciledAdvance & { id: string; selected: boolean };

/**
 * Zuordnung einer Belegzeile.
 *
 * TARGET_NEW  — es entsteht eine neue Position. Das ist die Vorbelegung fuer
 *               ALLES, was sich nicht eindeutig zuordnen laesst: die Leistung
 *               steht auf einer bereits gestellten Rechnung, sie IST erbracht
 *               und abgerechnet. Fehlt sie hinterher im Projekt, zieht die
 *               naechste kumulative Rechnung einen Abschlag ab, dem keine
 *               Leistung gegenuebersteht — und blockiert (Projekt 6193: nur
 *               die Begehungen standen im LV, alle uebrigen Belegzeilen fanden
 *               nur schwache Aehnlichkeiten und waren nicht ankreuzbar).
 *               Eine Dublette ist im LV sichtbar und korrigierbar, eine
 *               fehlende Leistung faellt erst Monate spaeter auf.
 * TARGET_UNSET— noch nicht entschieden; solange eine solche Zeile in der
 *               Tabelle steht, ist die Uebernahme gesperrt. Wird nicht mehr
 *               vorbelegt, bleibt aber als Riegel bestehen.
 * sonst       — id der bestehenden Position, auf die gebucht wird.
 */
const TARGET_NEW = BACKFILL_TARGET_NEW;
const TARGET_UNSET = BACKFILL_TARGET_UNSET;

/** `useLvPrice`: bei Preisabweichung auf den LV-Preis statt den Belegpreis wechseln. */
type PositionRow = ReconciledPosition & {
    id: string;
    selected: boolean;
    useLvPrice: boolean;
    target: string;
};

const priceOf = (r: PositionRow): number =>
    r.useLvPrice && r.existing ? r.existing.unitPriceCents : r.extracted.unitPriceCents;

/** Die zugeordnete bestehende Position, falls die Zeile auf eine bucht. */
const targetServiceOf = (r: PositionRow, services: ServiceItem[]): ServiceItem | undefined =>
    r.target === TARGET_NEW || r.target === TARGET_UNSET
        ? undefined
        : services.find((s) => s.id === r.target);

/**
 * Der Einzelpreis, mit dem die Menge tatsaechlich zu Buche schlaegt.
 *
 * Auf eine bestehende Position gebucht gilt deren LV-Preis — der Nachtrag
 * traegt Leistung nach und schreibt keinen Preis um. Die Vorschau muss deshalb
 * mit DIESEM Preis rechnen; mit dem Belegpreis zu summieren zeigte einen
 * Betrag, den das Projekt danach nirgends ausweist.
 */
const effectivePriceOf = (r: PositionRow, services: ServiceItem[]): number => {
    const target = targetServiceOf(r, services);
    return target ? centsFromEuro(target.unitPrice || 0) : priceOf(r);
};

/**
 * Die Zielposition, falls sie in einer ANDEREN Einheitenklasse steht als die
 * Belegzeile — sonst null.
 *
 * Uebernommen wird die rohe Belegmenge zum Preis der Zielposition. Stehen
 * "89 Std" einer Stueck-Position gegenueber, entstuenden daraus 89 Stueck zum
 * Stueckpreis: eine erfundene Leistung und ein erfundener Betrag, beides
 * dauerhaft gespeichert. Solche Zeilen SPERREN die Uebernahme, statt nur zu
 * warnen — umgerechnet wird nicht, weil es keinen hinterlegten Faktor zwischen
 * Stunden und Stueck gibt.
 */
const unitMismatchTarget = (r: PositionRow, services: ServiceItem[]): ServiceItem | null => {
    const target = targetServiceOf(r, services);
    return target && unitMismatch(r.extracted.unit, target.unit) ? target : null;
};

const targetLabel = (s: ServiceItem): string =>
    [s.lvPosition, s.title].filter(Boolean).join(' · ') || 'Position ohne Bezeichnung';

/** Ab dieser relativen Differenz wird die Gegenrechnung sichtbar gemeldet. */
const SUM_DEVIATION_WARN = 0.01;

/**
 * Ab dieser Differenz ist die Positionsliste nicht mehr ungenau, sondern
 * unvollstaendig — und wird gesperrt statt nur gemeldet.
 *
 * Eine Zeile, die die Erkennung nicht gefunden hat, fehlt hinterher im
 * Leistungsstand des Projekts. Genau daran blockiert spaeter die kumulative
 * Rechnung: sie zieht Abschlaege ab, denen keine Leistung gegenuebersteht, und
 * niemand sieht dem Projekt noch an, WELCHE Position fehlt (Projekt 5956 —
 * die fehlende Stundenzeile kostete 2.800,00 EUR Deckung und drei
 * Diagnoserunden). Ein Hinweis, den man wegklicken kann, reicht dafuer nicht.
 *
 * Die Sperre haengt an der AUSWAHL, nicht am Beleg: wer die Positionen abwaehlt
 * und nur die Abschlaege uebernehmen will, kommt weiterhin durch — der
 * Abschlagsteil steht fuer sich und ist von der Luecke nicht betroffen.
 */
const SUM_DEVIATION_BLOCK = 0.05;

const LastInvoiceImportModal: React.FC<{
    appDeductions: AdvanceDeduction[];
    externals: ExternalAdvance[];
    defaultTaxRateBp: number;
    /** Ist-Stand des Projekt-LV fuer den Positionsabgleich. */
    projectServices: ServiceItem[];
    /** Bereits per Backfill uebernommene Belegnummern (ALT-<Nummer>). */
    usedBackfillReferences?: Set<string>;
    /** Ohne Speicherpfad des Hosts bleibt die Positions-Sektion lesend. */
    canBackfillPositions: boolean;
    /** Beteiligte des Aufteilungsschluessels; ab zwei ist die Auswahl Pflicht. */
    splitParticipants?: { partyId: string; customerId: string; name: string }[] | null;
    onClose: () => void;
    onApply: (payload: {
        advances: { invoiceNumber: string; invoiceDate?: string; grossCents: number; taxRateBp: number }[];
        positions: BackfillSelection[];
        serviceDate: string;
        invoiceNumber: string;
        /**
         * Bestaetigter Nachlass der Alt-Rechnung in Cent, sonst 0.
         *
         * Aendert keine Zahl — der Abzug steht ohnehin mit dem GEFORDERTEN
         * Betrag in external_advances, und die Positionen kommen mit ihrem
         * vollen Einzelpreis ins LV. Er wandert allein in den Vermerk, damit
         * spaeter nachvollziehbar ist, warum Positionssumme und Belegsumme
         * auseinandergehen. Ohne den Vermerk sieht derselbe Beleg beim
         * naechsten Blick wieder nach einer fehlenden Zeile aus.
         */
        discountCents: number;
        /**
         * Auftraggeber des Altbelegs. Leer bei ungeteilten Projekten — dort
         * gehoert der Beleg dem einen Auftraggeber, den das Projekt hat.
         */
        customerId?: string;
        /**
         * Der TEILNEHMER der Aufteilung, dem der Altbeleg gehoert
         * (project_splits.id). Feiner als `customerId` und die massgebliche
         * Angabe, sobald ein Auftraggeber mehrere Abschnitte hat: sonst zoege
         * die Rechnung fuer Haus B den Abschlag von Haus A mit ab.
         */
        partyId?: string;
    }) => Promise<void>;
}> = ({
    appDeductions,
    externals,
    defaultTaxRateBp,
    projectServices,
    usedBackfillReferences,
    canBackfillPositions,
    splitParticipants,
    onClose,
    onApply,
}) => {
    const [phase, setPhase] = useState<'select' | 'loading' | 'review' | 'error'>('select');
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState('');
    const [rows, setRows] = useState<ReviewRow[]>([]);
    const [posRows, setPosRows] = useState<PositionRow[]>([]);
    /**
     * Liefert die Serverfunktion ueberhaupt ein Feld "positionen"? Eine noch
     * nicht aktualisierte Edge-Function laesst es weg — dann fehlt die
     * Positionserkennung, der Abschlagsteil laeuft aber unveraendert.
     */
    const [positionsSupported, setPositionsSupported] = useState(true);
    /**
     * Belegzeilen, die die Erkennung nicht uebernehmen konnte.
     *
     * Sie MUESSEN sichtbar sein: eine hier verschluckte Leistung fehlt
     * anschliessend im Projekt, und die naechste kumulative Rechnung blockiert
     * Monate spaeter an der fehlenden Deckung, ohne die Position zu nennen.
     */
    const [skippedRows, setSkippedRows] = useState<SkippedPositionRow[]>([]);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [serviceDate, setServiceDate] = useState('');
    /**
     * Auftraggeber des Altbelegs. Bewusst OHNE Vorbelegung, auch wenn nur zwei
     * Beteiligte in Frage kommen: geraten waere hier eine Geldforderung an den
     * Falschen, und ein vorbelegtes Feld wird uebersehen.
     */
    // Gewaehlt wird der TEILNEHMER (partyId). Der Auftraggeber wird daraus
    // ABGELEITET und nicht selbst gehalten: zwei Groessen, die dasselbe meinen,
    // laufen frueher oder spaeter auseinander.
    const [backfillPartyId, setBackfillPartyId] = useState('');
    const backfillParty = (splitParticipants ?? []).find((p) => p.partyId === backfillPartyId);
    // Die ALT-Referenz bleibt am AUFTRAGGEBER haengen ('ALT-<Nr>#<kunde>'):
    // sie identifiziert bereits eingelesene Belege, und ein Wechsel des
    // Unterscheidungsmerkmals liesse jeden Altbestand als "noch nicht
    // eingelesen" erscheinen — die Menge zaehlte beim naechsten Einlesen
    // doppelt.
    const backfillCustomerId = backfillParty?.customerId ?? '';
    const [deviation, setDeviation] = useState<SumDeviation | null>(null);
    /**
     * Hat der Nutzer bestaetigt, dass die Abweichung ein Nachlass ist?
     *
     * Die alten Teilleistungsrechnungen trugen einen Nachlass: ihre Zeilen
     * ergeben mehr, als der Beleg gefordert hat. Das sieht wie eine fehlende
     * Position aus, ist aber das Gegenteil — es fehlt nichts, es wurde weniger
     * verlangt. Nur der Mensch mit dem Beleg vor sich kann das entscheiden,
     * deshalb wird gefragt statt geraten.
     */
    const [discountConfirmed, setDiscountConfirmed] = useState(false);
    const [applying, setApplying] = useState(false);
    /**
     * Fehlschlag der UEBERNAHME — getrennt von `error`, das den Auslesevorgang
     * meldet und mit `phase === 'error'` die ganze Auswahl ersetzt.
     *
     * Bis hierher blieb ein Fehlschlag der Uebernahme unsichtbar: der Aufrufer
     * faengt ihn, setzt die Fehlermeldung des PANELS — und die steht hinter
     * diesem Dialog, der geoeffnet bleibt. Der Anwender sah nur, wie der
     * Fortschrittsring aufhoerte, und nichts passierte. Seit die Uebernahme
     * eine Protokollzeile serverseitig entfernt (`service_log_remove_entries`,
     * Migration Nr. 66), kann sie an einer echten Ursache scheitern, und dann
     * darf sie nicht mehr still aussehen wie ein Klick ins Leere.
     *
     * Die Auswahl bleibt dabei stehen: sie ist muehsam zusammengeklickt, und
     * ein zweiter Versuch soll sie nicht noch einmal verlangen.
     */
    const [applyError, setApplyError] = useState<string | null>(null);
    /**
     * Zaehler statt Flag: dragleave feuert auch beim Wechsel auf ein KIND der
     * Dropzone (Icon, Text). Ein boolesches Flag flackerte dadurch, sobald der
     * Zeiger ueber die Beschriftung faehrt.
     */
    const [dragDepth, setDragDepth] = useState(0);
    const fileRef = useRef<HTMLInputElement>(null);

    const runExtraction = async (file: File) => {
        // Beim Ziehen greift das accept-Attribut des Inputs nicht, und im
        // Dateidialog laesst sich "Alle Dateien" waehlen. Ohne diese Schranke
        // ginge eine beliebige Datei an die Erkennung — teuer und ergebnislos.
        if (!isSupportedFile(file)) {
            setError('Nur PDF oder Bild — diese Datei kann nicht ausgelesen werden.');
            setPhase('error');
            return;
        }
        if (file.size > MAX_BYTES) {
            setError('Die Datei ist größer als 18 MB — bitte ein kleineres PDF oder Bild verwenden.');
            setPhase('error');
            return;
        }
        setPhase('loading');
        setFileName(file.name);
        setError(null);
        try {
            const base64 = await fileToBase64(file);
            const parsed = await extractLastInvoice(base64, file.type || 'application/pdf');
            const extracted = extractedAdvancesFromAi(parsed, defaultTaxRateBp);
            const meta = extractedInvoiceMetaFromAi(parsed);
            const { positions, skipped } = extractPositionRows(parsed);
            const hasPositionsField = Array.isArray((parsed as { positionen?: unknown })?.positionen);
            if (!extracted.length && !positions.length) {
                setError('Im Dokument wurden weder Abschläge noch Leistungspositionen erkannt.');
                setPhase('error');
                return;
            }
            const reconciled = reconcileAdvances(
                extracted,
                aggregateExistingAdvances(appDeductions, externals),
            );
            setRows(
                reconciled.map((r, i) => ({
                    ...r,
                    id: `row-${i}`,
                    // Nur Neues wird vorgemerkt; Abweichungen entscheidet der
                    // Nutzer bewusst, Vorhandenes braucht nichts.
                    selected: r.status === 'new',
                })),
            );
            setPositionsSupported(hasPositionsField);
            // Nur die echten Verluste zeigen: Summen- und Abzugszeilen fallen
            // gewollt heraus und waeren hier reines Rauschen.
            setSkippedRows(skipped.filter((r) => r.reason !== 'nonService'));
            // Frueher startete die Auswahl leer, sobald dieser Beleg schon
            // einmal uebernommen worden war — ein Riegel gegen die Dublette.
            // Den braucht es nicht mehr: die Uebernahme ERSETZT den vorherigen
            // Lauf (revertBackfill), statt neben ihn zu treten. Abgewaehlte
            // Zeilen waeren jetzt der teurere Fehler, denn wer die Rechnung
            // korrigiert erneut einliest, will genau diese Zeilen schreiben.
            setPosRows(
                reconcilePositions(positions, projectServices).map((r, i) => ({
                    ...r,
                    id: `pos-${i}`,
                    // Vorangehakt ist ALLES, was im Projekt fehlt — das ist der
                    // Zweck des Imports. Nur 'matched' (steht identisch im LV,
                    // gar nicht ankreuzbar) und 'price_mismatch' (steht im LV,
                    // nur der Preis weicht ab — ein Haken legte eine ZWEITE
                    // Position an und verdoppelte den Leistungsstand) bleiben
                    // aussen vor.
                    //
                    // 'suggested' zaehlt zu den fehlenden: mit genau EINEM
                    // starken Kandidaten wird die Menge auf die bestehende
                    // Position gebucht, sonst wird neu angelegt (initialTarget).
                    // Uneindeutig heisst nicht "nicht abgerechnet" — die Zeile
                    // steht auf einer gestellten Rechnung und muss im Projekt
                    // ankommen, sonst blockiert die naechste kumulative
                    // Rechnung an der fehlenden Deckung.
                    selected: isBackfillPreselected(r.status),
                    useLvPrice: false,
                    target: initialBackfillTarget(r),
                })),
            );
            setInvoiceNumber(meta.invoiceNumber);
            setServiceDate(meta.invoiceDate ?? '');
            setDeviation(positionsSumDeviation(positions, meta.netTotalCents));
            // Der naechste Beleg ist eine neue Frage. Ohne das Zuruecksetzen
            // truege er die Antwort des vorigen und liefe ungeprueft durch.
            setDiscountConfirmed(false);
            setPhase('review');
        } catch (e) {
            setError(describeError(e));
            setPhase('error');
        }
    };

    const apply = async () => {
        setApplying(true);
        setApplyError(null);
        try {
            await onApply({
                advances: rows.filter((r) => r.selected).map((r) => r.extracted),
                positions: canBackfillPositions
                    ? posRows
                          // Ohne Zuordnung wird NICHTS uebernommen: weder eine
                          // neue Position noch eine Buchung auf eine falsche.
                          .filter((r) => r.selected && r.target !== TARGET_UNSET)
                          .map((r) => ({
                              position: r.extracted,
                              unitPriceCents: priceOf(r),
                              targetServiceId: r.target === TARGET_NEW ? undefined : r.target,
                          }))
                    : [],
                serviceDate,
                invoiceNumber,
                discountCents:
                    discountCandidate && discountConfirmed ? discountCandidate.amountCents : 0,
                customerId: backfillCustomerId || undefined,
                partyId: backfillPartyId || undefined,
            });
        } catch (e) {
            // Gefangen und ANGEZEIGT, nicht verschluckt: der Dialog bleibt
            // offen, die Auswahl steht noch, und der Grund steht in der
            // Fusszeile. Der Aufrufer hat denselben Fehler bereits in seine
            // eigene Meldung geschrieben — die wird sichtbar, sobald der
            // Dialog geschlossen ist.
            setApplyError(describeError(e));
        } finally {
            setApplying(false);
        }
    };

    const badge = (r: ReviewRow) => {
        if (r.status === 'matched')
            return <span className="text-[11px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded px-2 py-0.5 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> vorhanden</span>;
        if (r.status === 'mismatch')
            return <span className="text-[11px] font-bold uppercase bg-amber-50 text-amber-700 rounded px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> abweichend</span>;
        return <span className="text-[11px] font-bold uppercase bg-blue-50 text-blue-600 rounded px-2 py-0.5">neu</span>;
    };

    const posBadge = (r: PositionRow) => {
        if (r.status === 'matched')
            return <span className="text-[11px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded px-2 py-0.5 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> vorhanden</span>;
        if (r.status === 'price_mismatch')
            return <span className="text-[11px] font-bold uppercase bg-amber-50 text-amber-700 rounded px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Preisabweichung</span>;
        if (r.status === 'suggested')
            return <span className="text-[11px] font-bold uppercase bg-violet-50 text-violet-700 rounded px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> ähnlich vorhanden</span>;
        return <span className="text-[11px] font-bold uppercase bg-blue-50 text-blue-600 rounded px-2 py-0.5">neu</span>;
    };

    /**
     * Warnt, wenn bewusst eine Dublette entsteht: gleicher Einzelpreis, gleiche
     * Leistung dem Titel nach — und trotzdem "Neu anlegen". Genau so entstand
     * der Fall, den die Vorschlagsliste aufloest.
     */
    const duplicateCandidate = (r: PositionRow) =>
        r.target === TARGET_NEW
            ? r.candidates?.find((c) => c.exactUnitPrice) ?? null
            : null;

    // Funktionaler Updater: zwei Aenderungen im selben Tick (Haken + Zuordnung)
    // wuerden ueber die geschlossene posRows-Kopie sonst eine davon verlieren.
    const setRow = (id: string, patch: Partial<PositionRow>) =>
        setPosRows((rows) => rows.map((x) => (x.id === id ? { ...x, ...patch } : x)));

    const selectedCount = rows.filter((r) => r.selected).length;
    // Was der Alles-/Nichts-Schalter im Tabellenkopf ueberhaupt anfassen darf:
    // dieselbe Bedingung wie das `disabled` der Zeilenhaken. Ein Schalter, der
    // gesperrte Zeilen mitnaehme, erzeugte genau die Doppelbuchung, gegen die
    // die Sperre steht.
    const selectableAdvanceIds = new Set(rows.filter((r) => r.status === 'new').map((r) => r.id));
    // Ohne Speicherpfad des Hosts wird KEINE Position uebernommen (apply()
    // schickt dann []). Die Auswahl hier trotzdem mitzuzaehlen sperrte den
    // Dialog ueber das Datums-Gate — und zwar ohne Ausweg, denn das
    // Datumsfeld wird in diesem Fall gar nicht gerendert.
    const selectedPositions = canBackfillPositions
        ? posRows.filter((r) => r.selected && r.target !== TARGET_UNSET)
        : [];
    const selectablePositionIds = new Set(
        posRows.filter((r) => r.status !== 'matched' && r.target !== TARGET_UNSET).map((r) => r.id),
    );
    /**
     * Zeilen mit Vorschlaegen, aber ohne Entscheidung.
     *
     * Sie SPERREN die Uebernahme, statt uebersprungen zu werden: die Uebernahme
     * verbraucht die Referenz ALT-<Nummer>: derselbe Beleg meldet danach
     * "bereits übernommen", und die übersprungene Menge fehlte dauerhaft, ohne
     * dass es auffiele. Wer eine Zeile nicht will, stellt sie auf „Neu anlegen"
     * und nimmt den Haken heraus — eine Entscheidung, kein Versehen.
     */
    const unassignedCount = canBackfillPositions
        ? posRows.filter((r) => r.status === 'suggested' && r.target === TARGET_UNSET).length
        : 0;
    const blockedByUnassigned = canBackfillPositions && hasUnassignedBackfillRows(posRows);
    // Zeilen, die Stunden auf eine Stueck-Position (oder umgekehrt) buchen
    // wuerden. Sie sperren die Uebernahme, bis sie umgehaengt, auf „Neu
    // anlegen“ gestellt oder abgewaehlt sind — siehe unitMismatchTarget.
    const unitMismatchRows = selectedPositions.filter((r) => unitMismatchTarget(r, projectServices));
    const blockedByUnitMismatch = unitMismatchRows.length > 0;
    // Zugeordnete Zeilen schlagen mit dem LV-Preis der Zielposition zu Buche,
    // nicht mit dem der Alt-Rechnung — sonst nennt die Fusszeile einen Betrag,
    // den das Projekt hinterher nirgends ausweist.
    const addedNetCents = sumCents(
        selectedPositions.map((r) =>
            roundCents(effectivePriceOf(r, projectServices) * r.extracted.quantity),
        ),
    );
    const showDeviation = deviation !== null && deviation.ratio > SUM_DEVIATION_WARN;
    // Am BELEG festgemacht, damit der Banner seine Dringlichkeit nicht
    // wechselt, sobald jemand eine Zeile abwaehlt — die Lueckenmeldung gilt
    // dem Dokument, nicht der Auswahl.
    const severeDeviation = deviation !== null && deviation.ratio > SUM_DEVIATION_BLOCK;
    /**
     * Ein Nachlass, der die Abweichung erklaeren wuerde — oder null.
     *
     * Nur bei Positionen ueber der Belegsumme. Andersherum fehlt eine Zeile,
     * und daran darf sich nichts vorbeimogeln.
     */
    const discountCandidate = discountFromDeviation(deviation);
    const blockedBySumDeviation =
        severeDeviation && selectedPositions.length > 0 && !(discountCandidate && discountConfirmed);
    // Ein Protokolleintrag ohne Datum faellt aus jeder Zeitraumauswertung —
    // genau der Fehler, den der datierte Eintrag verhindern soll.
    const missingServiceDate = selectedPositions.length > 0 && !serviceDate;
    // Eine Belegnummer ist Pflicht, sobald ueberhaupt Positionen uebernommen
    // werden: ohne sie entsteht eine bookingReference 'ALT-' ohne Herkunft
    // (siehe backfillReferenceFor) — nicht mehr auffindbar und nicht mehr von
    // einer anderen unlesbaren Alt-Rechnung zu unterscheiden. Greift wie
    // missingServiceDate nur, wenn der Backfill-Pfad ueberhaupt aktiv ist:
    // selectedPositions ist bei !canBackfillPositions bereits [].
    const missingInvoiceNumber = selectedPositions.length > 0 && !invoiceNumber.trim();
    // Banner ueber der Positions-Tabelle: die AKTUELLE (editierbare)
    // Beleg-Nr. gegen die bereits genutzten ALT-Referenzen pruefen — nicht
    // nur die urspruenglich extrahierte, damit eine Korrektur der Nummer im
    // Feld die Warnung sofort aufloest oder wieder aufleben laesst.
    const alreadyBackfilledForNumber = Boolean(
        invoiceNumber.trim() &&
            usedBackfillReferences?.has(
                backfillReferenceFor(invoiceNumber, backfillCustomerId || undefined),
            ),
    );
    /**
     * Geteiltes Projekt ohne Angabe des Auftraggebers: gesperrt.
     *
     * Die Menge kaeme sonst als Altbestand ohne Zuordnung ins Projekt und
     * sperrte dort jede weitere Teilrechnung — nur an einer Stelle, an der es
     * niemand mehr mit diesem Import in Verbindung bringt. Lieber hier
     * anhalten, wo der Beleg noch auf dem Tisch liegt.
     */
    const isSplitProject = (splitParticipants?.length ?? 0) > 1;
    /**
     * Der Aufteilungsschluessel steht noch nicht fest (laedt, oder das Laden
     * ist fehlgeschlagen).
     *
     * Dann wird NICHT uebernommen. Ein Fehlschlag ist keine Auskunft: den
     * Beleg jetzt ohne Auftraggeber einzulesen erzeugte Abrechnungsbestand
     * ohne Zuordnung, und der faellt erst Wochen spaeter auf — an einer Stelle,
     * die niemand mehr mit diesem Import in Verbindung bringt.
     */
    const splitUnbekannt = splitParticipants === null;
    const missingBackfillCustomer =
        selectedPositions.length > 0 &&
        (splitUnbekannt || (isSplitProject && !backfillPartyId));

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
                <div className="bg-slate-800 text-white p-5 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5" /> Letzte Rechnung einlesen</h3>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                    {phase === 'select' && (
                        /* Klicken ODER hineinziehen. onDragOver MUSS
                           preventDefault rufen, sonst oeffnet der Browser die
                           Datei einfach im Tab und der Dialog ist weg. */
                        <label
                            onDragEnter={(e) => { e.preventDefault(); setDragDepth((d) => d + 1); }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragDepth(0);
                                // Nur die erste Datei: eingelesen wird EIN Beleg.
                                const f = e.dataTransfer.files?.[0];
                                if (f) void runExtraction(f);
                            }}
                            className={`flex flex-col items-center justify-center gap-2 h-44 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                                dragDepth > 0
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                                    : 'border-slate-300 hover:border-indigo-400 hover:bg-white text-slate-400'
                            }`}
                        >
                            <Upload className="w-8 h-8" />
                            <span className="text-sm">
                                {dragDepth > 0 ? 'Datei hier loslassen' : 'PDF oder Bild der letzten Abschlagsrechnung hierher ziehen oder wählen'}
                            </span>
                            <span className="text-[11px] text-center max-w-md">Der Abzugsblock nennt alle Vorrechnungen, die Positionsliste die bereits berechnete Leistung — beides wird mit dem Bestand abgeglichen, nichts wird automatisch gespeichert.</span>
                            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void runExtraction(f); e.target.value = ''; }} />
                        </label>
                    )}
                    {phase === 'loading' && (
                        <div className="h-44 flex flex-col items-center justify-center gap-3 text-slate-500">
                            <Loader2 className="w-8 h-8 animate-spin" />
                            <span className="text-sm">{fileName} wird ausgelesen…</span>
                        </div>
                    )}
                    {phase === 'error' && (
                        <div className="h-44 flex flex-col items-center justify-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-amber-500" />
                            <p className="text-sm text-slate-600 text-center">{error}</p>
                            <Button variant="secondary" onClick={() => setPhase('select')}>Erneut versuchen</Button>
                        </div>
                    )}
                    {phase === 'review' && (
                        <>
                        {/* Der Haken war die am haeufigsten missverstandene Stelle des
                            Dialogs: die Spalte trug keine Ueberschrift, und dass NUR
                            angehakte Zeilen uebernommen werden, stand nirgends. Beides
                            steht jetzt da, samt Alles-/Nichts-Schalter im Kopf. */}
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Schritt 1 — Abschläge (Abzug künftiger Rechnungen)</h4>
                        <p className="text-[11px] text-slate-500 mb-2">
                            <strong>Angehakt = wird übernommen.</strong> Alles ohne Haken bleibt außen vor. Bereits hinterlegte Abschläge sind nicht anhakbar — sie stehen schon im Projekt.
                        </p>
                        <table className="w-full text-left text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs">
                                <tr>
                                    <th className="p-3 w-24">
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                type="checkbox"
                                                title="Alle übernehmbaren Abschläge an- oder abwählen"
                                                disabled={selectableAdvanceIds.size === 0}
                                                checked={selectableAdvanceIds.size > 0 && selectedCount === selectableAdvanceIds.size}
                                                onChange={(e) => setRows(rows.map((x) => (selectableAdvanceIds.has(x.id) ? { ...x, selected: e.target.checked } : x)))}
                                                className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                                            />
                                            <span>Übernehmen</span>
                                        </div>
                                    </th>
                                    <th className="p-3">Nummer</th>
                                    <th className="p-3">Datum</th>
                                    <th className="p-3 text-right">Brutto</th>
                                    <th className="p-3 text-right">USt.</th>
                                    <th className="p-3">Abgleich</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => (
                                    <tr key={r.id} className={r.selected ? 'bg-indigo-50/50' : undefined}>
                                        <td className="p-3">
                                            {/* Nur Neues ist uebernehmbar: eine abweichende Zeile
                                                als ZWEITEN Eintrag anzulegen hiesse denselben
                                                Altbeleg doppelt abziehen — Abweichungen werden im
                                                Editor am bestehenden Eintrag korrigiert. */}
                                            <input
                                                type="checkbox"
                                                disabled={r.status !== 'new'}
                                                title={r.status === 'new'
                                                    ? 'Angehakt wird dieser Abschlag ins Projekt übernommen'
                                                    : 'Steht bereits im Projekt — ein zweiter Eintrag zöge denselben Altbeleg doppelt ab'}
                                                checked={r.selected}
                                                onChange={(e) => setRows(rows.map((x) => (x.id === r.id ? { ...x, selected: e.target.checked } : x)))}
                                                className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                                            />
                                        </td>
                                        <td className="p-3 font-mono">{r.extracted.invoiceNumber}</td>
                                        <td className="p-3 text-slate-500">{de(r.extracted.invoiceDate)}</td>
                                        <td className="p-3 text-right font-mono">
                                            {formatCents(r.extracted.grossCents)}
                                            {r.status === 'mismatch' && r.existing && (
                                                <div className="text-[11px] text-amber-700">hinterlegt: {formatCents(r.existing.grossCents)}</div>
                                            )}
                                        </td>
                                        <td className="p-3 text-right text-slate-500">{formatTaxRateBp(r.extracted.taxRateBp)}</td>
                                        <td className="p-3">{badge(r)}</td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={6} className="p-4 text-center text-slate-400 italic text-xs">Kein Abzugsblock erkannt.</td></tr>
                                )}
                            </tbody>
                        </table>

                        {/* Positionen der Alt-Rechnung: ohne sie zieht die naechste
                            kumulative Rechnung Abschlaege ab, denen keine Leistung
                            im Projekt gegenuebersteht — und blockiert. */}
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-6 mb-1">Schritt 2 — Positionen (Leistung des Projekts)</h4>
                        {!positionsSupported ? (
                            <p className="text-xs text-slate-500 bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                                Positionserkennung erfordert eine aktualisierte Serverfunktion. Die Abschläge oben wurden normal eingelesen; Alt-Positionen können erst nach dem Update übernommen werden.
                            </p>
                        ) : !canBackfillPositions ? (
                            <p className="text-xs text-slate-500 bg-white border border-slate-200 rounded-xl p-4">
                                Positionen können in dieser Ansicht nur angezeigt, nicht übernommen werden.
                            </p>
                        ) : posRows.length === 0 ? (
                            <p className="text-xs text-slate-500 bg-white border border-slate-200 rounded-xl p-4">
                                Im Dokument wurden keine Leistungspositionen mit Betrag erkannt.
                                {skippedRows.length > 0 && (
                                    <>
                                        {' '}Nicht lesbar waren: {skippedRows.map((r) => `„${r.title || 'ohne Bezeichnung'}“`).join(', ')}.
                                    </>
                                )}
                            </p>
                        ) : (
                            <>
                                {alreadyBackfilledForNumber && (
                                    <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-xl p-3 mb-2 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                        Beleg {invoiceNumber.trim()} wurde bereits eingelesen. Die Übernahme <strong>ersetzt</strong> den vorherigen Stand — die Mengen zählen nicht doppelt.
                                    </p>
                                )}
                                <div className="bg-white border border-slate-200 rounded-xl p-3 mb-2 flex flex-wrap items-end gap-4">
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Leistungsdatum</label>
                                        <input
                                            type="date"
                                            value={serviceDate}
                                            onChange={(e) => setServiceDate(e.target.value)}
                                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        {/* Das Datum hat Wirkung: Leistung, die danach
                                            protokolliert ist, steckt nicht im Beleg und
                                            bleibt beim Anheben offen (nextUsed). */}
                                        <p className="mt-1 text-[11px] text-slate-400 max-w-[16rem]">
                                            Bis zu diesem Tag deckt der Beleg die Leistung. Später erfasste Leistung bleibt offen.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Beleg-Nr.</label>
                                        <input
                                            type="text"
                                            value={invoiceNumber}
                                            onChange={(e) => setInvoiceNumber(e.target.value)}
                                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    {isSplitProject && (
                                        <div>
                                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Gehört zu</label>
                                            <select
                                                value={backfillPartyId}
                                                onChange={(e) => setBackfillPartyId(e.target.value)}
                                                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                                            >
                                                <option value="">— bitte wählen —</option>
                                                {(splitParticipants ?? []).map((p) => (
                                                    <option key={p.partyId} value={p.partyId}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <p className="text-[11px] text-slate-400 flex-1 min-w-[16rem]">
                                        Gilt für alle übernommenen Positionen — es datiert den Protokolleintrag, damit eine Zeitraumabrechnung die Leistung findet. Vorbelegt mit dem Rechnungsdatum.
                                        {isSplitProject && ' Der Auftraggeber sagt, wessen Anteil dieser Beleg abgegolten hat — das Projekt ist auf mehrere aufgeteilt, und der Beleg deckt nur einen Teil der Menge.'}
                                    </p>
                                </div>
                                {skippedRows.length > 0 && (
                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                        <span>
                                            {skippedRows.length} Zeile(n) des Belegs konnten nicht gelesen werden und stehen deshalb nicht in der Tabelle:{' '}
                                            {skippedRows
                                                .map((r) => `„${r.title || 'ohne Bezeichnung'}“ (${r.reason === 'inconsistent' ? 'Einzelpreis und Zeilensumme passen nicht zusammen' : 'kein Betrag erkennbar'})`)
                                                .join(', ')}. Sind das Leistungen, müssen sie von Hand ins LV — sonst fehlen sie im Leistungsstand und die nächste Rechnung blockiert daran.
                                        </span>
                                    </p>
                                )}
                                {/* Zwei Richtungen, zwei Fehlerbilder. Frueher stand hier in
                                    beiden Faellen "es fehlt eine Zeile" — bei einem Nachlass
                                    war das schlicht falsch und die Uebernahme unaufloesbar
                                    gesperrt. */}
                                {showDeviation && deviation && !discountCandidate && (
                                    <p className={`text-xs rounded-xl p-3 mb-2 flex items-start gap-2 border ${severeDeviation ? 'text-red-700 bg-red-50 border-red-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
                                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                        {severeDeviation ? (
                                            <>Die erkannten Positionen ergeben {formatCents(deviation.sumCents)}, der Beleg weist {formatCents(deviation.referenceCents)} aus ({(deviation.ratio * 100).toFixed(1)} % Abweichung). Es fehlt mindestens eine Leistungszeile — würde sie hier fehlen, fehlt sie hinterher auch im Leistungsstand des Projekts und die nächste Rechnung blockiert daran. Die Übernahme ist gesperrt, solange Positionen ausgewählt sind.</>
                                        ) : (
                                            <>Die erkannten Positionen ergeben {formatCents(deviation.sumCents)}, der Beleg weist {formatCents(deviation.referenceCents)} aus ({(deviation.ratio * 100).toFixed(1)} % Abweichung). Vermutlich fehlt eine Zeile oder eine Summenzeile wurde mitgelesen — bitte vor dem Übernehmen prüfen.</>
                                        )}
                                    </p>
                                )}

                                {/* Positionen ueber der Belegsumme: das kann keine fehlende
                                    Zeile sein. Der Beleg hat WENIGER gefordert, als seine
                                    Zeilen ergeben — genau das tut ein Nachlass, und genau so
                                    sahen die alten Teilleistungsrechnungen aus. Entschieden
                                    wird das nicht geraten, sondern gefragt: nur wer den Beleg
                                    vor sich hat, weiss es. */}
                                {showDeviation && deviation && discountCandidate && (
                                    <div className={`text-xs rounded-xl p-3 mb-2 border ${discountConfirmed ? 'text-emerald-800 bg-emerald-50 border-emerald-200' : 'text-amber-800 bg-amber-50 border-amber-200'}`}>
                                        <p className="flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                            <span>
                                                Die erkannten Positionen ergeben{' '}
                                                <strong>{formatCents(deviation.sumCents)}</strong>, gefordert hat der Beleg{' '}
                                                <strong>{formatCents(deviation.referenceCents)}</strong> — er liegt{' '}
                                                <strong>{formatCents(discountCandidate.amountCents)}</strong> darunter
                                                ({discountCandidate.percent.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %).
                                                Eine fehlende Zeile kann das nicht sein; dann wäre die Summe zu klein, nicht zu groß.
                                                Kommt die Differenz aus einem <strong>Nachlass</strong>?
                                            </span>
                                        </p>
                                        <label className="mt-2 flex cursor-pointer items-start gap-2.5 leading-relaxed">
                                            <input
                                                type="checkbox"
                                                className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
                                                checked={discountConfirmed}
                                                onChange={(e) => setDiscountConfirmed(e.target.checked)}
                                            />
                                            <span>
                                                Ja — die Alt-Rechnung enthielt einen Nachlass von{' '}
                                                {formatCents(discountCandidate.amountCents)}. Die Mengen stimmen, nur der
                                                geforderte Betrag war niedriger. Die Positionen kommen mit ihrem vollen
                                                Einzelpreis ins Leistungsverzeichnis — der Nachlass gehört zum Beleg, nicht
                                                zum Vertrag —, und abgezogen wird der tatsächlich geforderte Betrag.
                                            </span>
                                        </label>
                                        {!discountConfirmed && severeDeviation && (
                                            <p className="mt-1.5 font-medium">
                                                Ohne diese Bestätigung bleibt die Übernahme gesperrt, solange Positionen
                                                ausgewählt sind. War es kein Nachlass, wurde vermutlich eine Summenzeile
                                                als Position mitgelesen — dann die betroffene Zeile abwählen.
                                            </p>
                                        )}
                                    </div>
                                )}
                                <p className="text-[11px] text-slate-500 mb-2">
                                    <strong>Angehakt = wird übernommen.</strong> Erst die <em>Zuordnung</em> wählen (bestehende Position oder „Neu anlegen“), der Haken setzt sich dabei von selbst. Zeilen, die schon so im Projekt stehen, sind nicht anhakbar.
                                </p>
                                <table className="w-full text-left text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
                                    <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs">
                                        <tr>
                                            <th className="p-3 w-24">
                                                <div className="flex items-center gap-1.5">
                                                    <input
                                                        type="checkbox"
                                                        title="Alle zugeordneten Positionen an- oder abwählen"
                                                        disabled={selectablePositionIds.size === 0}
                                                        checked={selectablePositionIds.size > 0
                                                            && posRows.filter((x) => selectablePositionIds.has(x.id) && x.selected).length === selectablePositionIds.size}
                                                        onChange={(e) => setPosRows(posRows.map((x) => (selectablePositionIds.has(x.id) ? { ...x, selected: e.target.checked } : x)))}
                                                        className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                                                    />
                                                    <span>Übernehmen</span>
                                                </div>
                                            </th>
                                            <th className="p-3">Position</th>
                                            <th className="p-3">Zuordnung</th>
                                            <th className="p-3 text-right">Menge</th>
                                            <th className="p-3 text-right">EP</th>
                                            <th className="p-3 text-right">Gesamt</th>
                                            <th className="p-3">Abgleich</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {posRows.map((r) => (
                                            <tr key={r.id} className={r.selected ? 'bg-indigo-50/50' : undefined}>
                                                <td className="p-3">
                                                    {/* 'matched' ist nicht ankreuzbar, 'price_mismatch' nicht
                                                        vorangehakt: die Position steht bereits im Projekt —
                                                        ein zweiter Eintrag verdoppelte den Leistungsstand.
                                                        Genau das macht einen erneuten Import derselben Datei
                                                        folgenlos. */}
                                                    <input
                                                        type="checkbox"
                                                        // Ohne Zuordnung ist die Zeile nicht
                                                        // uebernehmbar — sonst entschiede der
                                                        // Haken, was die Auswahl offenlaesst.
                                                        disabled={r.status === 'matched' || r.target === TARGET_UNSET}
                                                        title={r.status === 'matched'
                                                            ? 'Steht bereits genau so im Projekt — ein zweiter Eintrag verdoppelte den Leistungsstand'
                                                            : r.target === TARGET_UNSET
                                                              ? 'Erst zuordnen: bestehende Position wählen oder „Neu anlegen“'
                                                              : 'Angehakt wird diese Leistung ins Projekt übernommen'}
                                                        checked={r.selected}
                                                        onChange={(e) => setRow(r.id, { selected: e.target.checked })}
                                                        className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                                                    />
                                                </td>
                                                <td className="p-3">
                                                    <div className="font-medium text-slate-700">{r.extracted.title}</div>
                                                    {r.extracted.lvPosition && <div className="text-[11px] text-slate-400 font-mono">{r.extracted.lvPosition}</div>}
                                                    {/* Die Preisabweichung ist der harmlose Teil der Meldung.
                                                        Der gefaehrliche: die Position IST schon da — Ankreuzen
                                                        legt sie ein zweites Mal an. */}
                                                    {r.status === 'price_mismatch' && r.existing && (
                                                        <div className="text-[11px] text-amber-700 mt-1">
                                                            Bereits im Projekt: „{r.existing.title}“{r.existing.lvPosition ? ` (${r.existing.lvPosition})` : ''} zu {formatCents(r.existing.unitPriceCents)}. Ankreuzen legt eine <b>zusätzliche</b> Position an — nur wählen, wenn es wirklich eine zweite Leistung ist; sonst den Preis im LV korrigieren.
                                                        </div>
                                                    )}
                                                </td>
                                                {/* Zuordnung: entweder auf eine bestehende Position
                                                    buchen (dann entsteht KEINE zweite Position und die
                                                    Menge waechst dort) oder bewusst neu anlegen. Bei
                                                    'matched' ist die Zuordnung festgestellt und nicht
                                                    verhandelbar. */}
                                                <td className="p-3 align-top">
                                                    <select
                                                        value={r.target}
                                                        disabled={r.status === 'matched'}
                                                        onChange={(e) => {
                                                            const target = e.target.value;
                                                            setRow(r.id, {
                                                                target,
                                                                // Zurueck auf "Bitte zuordnen" heisst:
                                                                // Entscheidung offen, also auch kein Haken.
                                                                // Umgekehrt IST die erstmalige Zuordnung die
                                                                // Entscheidung — bliebe der Haken aus, fiele
                                                                // die eben zugeordnete Zeile stillschweigend
                                                                // aus der Uebernahme.
                                                                selected:
                                                                    target === TARGET_UNSET
                                                                        ? false
                                                                        : r.target === TARGET_UNSET
                                                                          ? true
                                                                          : r.selected,
                                                            });
                                                        }}
                                                        className="w-full max-w-[15rem] border border-slate-300 rounded-lg px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-400"
                                                    >
                                                        {r.target === TARGET_UNSET && (
                                                            <option value={TARGET_UNSET}>Bitte zuordnen …</option>
                                                        )}
                                                        <option value={TARGET_NEW}>Neu anlegen</option>
                                                        {projectServices.map((s) => (
                                                            <option key={s.id} value={s.id}>{targetLabel(s)}</option>
                                                        ))}
                                                    </select>
                                                    {r.status === 'suggested' && r.candidates?.length ? (
                                                        <div className="text-[11px] text-slate-400 mt-1">
                                                            Ähnlich im LV: „{r.candidates[0].existing.title}“
                                                            {r.candidates[0].exactUnitPrice ? ' (gleicher Einzelpreis)' : ''}
                                                            {r.target === TARGET_NEW
                                                                ? ' — nicht eindeutig, wird deshalb neu angelegt. Ist es dieselbe Leistung, hier zuordnen.'
                                                                : ''}
                                                        </div>
                                                    ) : null}
                                                    {r.target === TARGET_UNSET && (
                                                        <div className="text-[11px] text-amber-700 mt-1">
                                                            Mehrdeutig — bitte eine Position wählen oder „Neu anlegen“.
                                                        </div>
                                                    )}
                                                    {(() => {
                                                        const dup = duplicateCandidate(r);
                                                        return dup ? (
                                                            <div className="text-[11px] text-amber-700 mt-1">
                                                                Legt eine <b>zusätzliche</b> Position an, obwohl „{dup.existing.title}“
                                                                {dup.existing.lvPosition ? ` (${dup.existing.lvPosition})` : ''} mit demselben
                                                                Einzelpreis {formatCents(dup.existing.unitPriceCents)} existiert.
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                </td>
                                                <td className="p-3 text-right text-slate-500 whitespace-nowrap">
                                                    {r.extracted.quantity} {r.extracted.unit}
                                                    {/* Teilleistung: die Position ist NICHT fertig. Das muss
                                                        vor der Uebernahme dastehen — hinterher sieht man der
                                                        Position nur noch an, dass etwas offen ist, nicht warum. */}
                                                    {r.extracted.sharePercent != null && r.extracted.contractedQuantity != null && (
                                                        <div className="text-[11px] text-violet-700 mt-1 font-sans whitespace-normal">
                                                            {r.extracted.sharePercent} % von {r.extracted.contractedQuantity} {r.extracted.unit || 'Einheit(en)'} — der Rest bleibt {r.target === TARGET_NEW ? 'als offene Menge stehen' : 'an der Zielposition offen'}.
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right font-mono">
                                                    {formatCents(effectivePriceOf(r, projectServices))}
                                                    {/* Auf eine bestehende Position gebucht wird nur die
                                                        MENGE — ihr Preis bleibt, wie er im LV steht. Der
                                                        Nachtrag traegt Leistung nach und verhandelt nicht
                                                        den Preis nach. Weicht er vom Beleg ab, muss das
                                                        dastehen: der Betrag der Uebernahme ist dann ein
                                                        anderer als der der Alt-Rechnung. */}
                                                    {(() => {
                                                        const target = targetServiceOf(r, projectServices);
                                                        if (!target) return null;
                                                        const lvCents = centsFromEuro(target.unitPrice || 0);
                                                        return lvCents === r.extracted.unitPriceCents ? (
                                                            <div className="text-[11px] text-slate-400 mt-1">LV-Preis bleibt</div>
                                                        ) : (
                                                            <div className="text-[11px] text-amber-700 mt-1 font-sans">
                                                                Wird zum LV-Preis {formatCents(lvCents)} statt {formatCents(r.extracted.unitPriceCents)} gebucht ({lvCents > r.extracted.unitPriceCents ? '+' : '−'}{formatCents(Math.abs(lvCents - r.extracted.unitPriceCents))} je Einheit).
                                                            </div>
                                                        );
                                                    })()}
                                                    {/* Einheitenklasse: Stunden gegen Stueck ist kein
                                                        Hinweis, sondern ein Riegel — die Menge wuerde mit
                                                        dem Preis der Zielposition multipliziert. */}
                                                    {(() => {
                                                        const clash = unitMismatchTarget(r, projectServices);
                                                        return clash ? (
                                                            <div className="text-[11px] text-red-700 mt-1 font-sans font-medium">
                                                                Andere Einheit: Beleg „{r.extracted.unit}“, Position „{clash.unit}“. Die Menge {r.extracted.quantity} würde unverändert zum Positionspreis {formatCents(centsFromEuro(clash.unitPrice || 0))} je {clash.unit} gebucht. Bitte eine Position derselben Einheit wählen, „Neu anlegen“ stellen oder die Zeile abwählen — umgerechnet wird nicht.
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                    {r.status === 'price_mismatch' && r.target === TARGET_NEW && r.existing && (
                                                        <div className="mt-1 flex flex-col items-end gap-0.5 text-[11px]">
                                                            {/* Vorgabe ist der Preis der Alt-Rechnung: er hat
                                                                den Betrag erzeugt, der als Abschlag abgezogen
                                                                wird. Der LV-Preis bleibt waehlbar. */}
                                                            <label className="flex items-center gap-1 cursor-pointer text-slate-500">
                                                                <input type="radio" checked={!r.useLvPrice} onChange={() => setRow(r.id, { useLvPrice: false })} />
                                                                Beleg {formatCents(r.extracted.unitPriceCents)}
                                                            </label>
                                                            <label className="flex items-center gap-1 cursor-pointer text-slate-500">
                                                                <input type="radio" checked={r.useLvPrice} onChange={() => setRow(r.id, { useLvPrice: true })} />
                                                                LV {formatCents(r.existing.unitPriceCents)}
                                                            </label>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right font-mono">{formatCents(roundCents(effectivePriceOf(r, projectServices) * r.extracted.quantity))}</td>
                                                <td className="p-3">{posBadge(r)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-slate-50 font-semibold text-slate-700">
                                            <td className="p-3" colSpan={5}>ergänzt {selectedPositions.length} Position(en) Leistung</td>
                                            <td className="p-3 text-right font-mono">{formatCents(addedNetCents)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                                <p className="text-[11px] text-slate-400 mt-2">
                                    Neu angelegte Positionen erhalten beauftragte = erledigte = abgerechnete Menge, Status „erledigt“ — die Leistung steht auf der Alt-Rechnung und ist damit erbracht und gefordert. Bei einer anteilig abgerechneten Teilleistung bleibt die beauftragte Menge voll und nur der Anteil gilt als erledigt und abgerechnet; die Position bleibt offen, damit der Rest abrechenbar bleibt. Einer bestehenden Position zugeordnete Zeilen heben deren erledigte und abgerechnete Menge auf den Stand des Belegs an; Preis und beauftragte Menge bleiben unverändert. Beides wird im Leistungsprotokoll auf die Alt-Rechnung {invoiceNumber || '—'} datiert.
                                </p>
                            </>
                        )}
                        </>
                    )}
                </div>
                {phase === 'review' && (
                    <div className="p-4 border-t border-slate-200 bg-white flex justify-between items-center gap-4 shrink-0">
                        <span className="text-xs text-slate-500">
                            {/* Zuerst der Fehlschlag des letzten Versuchs: er
                                betrifft genau den Knopf daneben, und ein
                                Hinweis auf die Auswahl waere daneben eine
                                Auskunft ueber etwas, das gar nicht das Problem
                                war.

                                Bewusst KEINE Zusicherung, dass nichts
                                geschrieben wurde: die Uebernahme schreibt erst
                                die Positionen ins Projekt und danach die
                                Abschlaege; bricht sie im zweiten Schritt ab,
                                steht der erste bereits. Ein erneutes Einlesen
                                desselben Belegs ERSETZT den vorherigen Lauf
                                (revertBackfill) und ist deshalb der richtige
                                zweite Versuch. */}
                            {applyError ? (
                                <span className="text-red-700 font-medium">Die Übernahme ist fehlgeschlagen: {applyError} Bitte den Stand im Projekt prüfen; ein erneutes Einlesen desselben Belegs ersetzt den vorherigen Lauf.</span>
                            ) : missingBackfillCustomer ? (
                                <span className="text-xs text-red-600 font-semibold">
                                    {splitUnbekannt
                                        ? 'Der Aufteilungsschlüssel des Projekts steht noch nicht fest. Bitte die Ansicht neu laden — ohne ihn ließe sich der Beleg keinem Auftraggeber zuordnen.'
                                        : 'Bitte den Auftraggeber wählen — das Projekt ist auf mehrere aufgeteilt.'}
                                </span>
                            ) : missingServiceDate ? (
                                <span className="text-amber-700 font-medium">Bitte ein Leistungsdatum wählen — ohne Datum findet keine Zeitraumabrechnung die nachgetragene Leistung.</span>
                            ) : missingInvoiceNumber ? (
                                <span className="text-amber-700 font-medium">Bitte eine Beleg-Nr. angeben — ohne Nummer bleibt die Herkunft der nachgetragenen Position nicht auffindbar.</span>
                            ) : blockedByUnassigned ? (
                                <span className="text-amber-700 font-medium">Bitte zuerst alle Positionen zuordnen oder auf „Neu anlegen“ stellen. ({unassignedCount} offen — nicht gewünschte Zeilen danach einfach abwählen.)</span>
                            ) : blockedByUnitMismatch ? (
                                <span className="text-red-700 font-medium">{unitMismatchRows.length} Zeile(n) buchen eine andere Einheit auf die Zielposition (z. B. Stunden auf eine Stück-Position) — die Menge ginge zum Positionspreis in die Abrechnung. Bitte umhängen, auf „Neu anlegen“ stellen oder abwählen.</span>
                            ) : blockedBySumDeviation && deviation && discountCandidate ? (
                                <span className="text-red-700 font-medium">Die Positionen ergeben {formatCents(deviation.sumCents)}, gefordert wurden {formatCents(deviation.referenceCents)}. Bitte oben bestätigen, dass die Differenz ein Nachlass ist — oder die mitgelesene Summenzeile abwählen.</span>
                            ) : blockedBySumDeviation && deviation ? (
                                <span className="text-red-700 font-medium">Die erkannten Positionen decken nur {formatCents(deviation.sumCents)} von {formatCents(deviation.referenceCents)} — es fehlt mindestens eine Zeile. Bitte die fehlende Position von Hand ergänzen oder alle Positionen abwählen und nur die Abschläge übernehmen.</span>
                            ) : selectedCount === 0 && selectedPositions.length === 0 ? (
                                /* Der Knopf ist in diesem Fall gesperrt, und ohne
                                   diesen Satz stand nirgends, warum. */
                                <span className="text-amber-700 font-medium">Nichts angehakt — in der Spalte „Übernehmen“ anhaken, was ins Projekt soll.</span>
                            ) : (
                                <>Angehakt: {selectedCount} von {rows.length} Abschlägen, {selectedPositions.length} von {posRows.length} Positionen. Nur diese werden übernommen. Abweichende Abschlagsbeträge bitte am bestehenden Eintrag korrigieren — sie werden hier nicht übernommen.</>
                            )}
                        </span>
                        <div className="flex gap-2 shrink-0">
                            <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
                            <Button variant="primary" onClick={() => void apply()} disabled={applying || missingBackfillCustomer || missingServiceDate || missingInvoiceNumber || blockedByUnassigned || blockedByUnitMismatch || blockedBySumDeviation || (selectedCount === 0 && selectedPositions.length === 0)} icon={applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}>
                                Übernehmen
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
